import React, { FC, useCallback, useEffect } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { Description, MainHeadline } from 'components/Typography'
import { NavButton, OrangeButton } from 'components/Button'
import { saveStoreSettings, updatePreorderDeadline } from 'reducks/store/operations'
import { useDispatch, useSelector } from 'react-redux'
import { goBack } from 'connected-react-router'
import { AlertCard, SettingsConfirmCardList } from 'components/Card'
import { State } from 'index'
import { getOpenTime, getHolidays } from 'reducks/store/selectors'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { getOrders } from 'reducks/checkout/selectors'
import { BreadType } from 'types/bread'
import { getBreads } from 'reducks/bread/selectors'
import { StockType } from 'types/stock'
import { getStocks } from 'reducks/stock/selectors'
import { ScheduleType } from 'types/schedule'
import { getSchedules } from 'reducks/schedule/selectors'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { StoreCalendarType } from 'types/storeCalendar'
import { OrderType } from 'types/checkout'
import { getStoreRef } from 'reducks/auth/selectors'
import { SpecialHolidayType } from 'types/calendar'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { fetchBreads } from 'reducks/bread/operations'
import { fetchSchedules } from 'reducks/schedule/operations'
import { fetchFutureStocks } from 'reducks/stock/operations'
import { fetchStoreCalendars, saveStoreCalendars } from 'reducks/storeCalendar/operations'
import { fetchFutureOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import {
  getConfirmHolidays,
  getConfirmLastOrderTime,
  getConfirmOpenTime,
  getConfirmOrderStartTime,
  getConfirmStore,
  getConfirmStoreCalendars,
  getConfirmStoreGeoSearch,
} from 'reducks/confirm/selectors'
import { BusinessTimeType, OpenTimeType, StoreType } from 'types/store'
import equal from 'fast-deep-equal'
import { db } from 'firebase/index'
import startOfMonth from 'date-fns/startOfMonth'
import {
  useConfirmCloseStoreCalendars,
  useConfirmAddedHolidays,
  useConfirmBreadList,
  useConfirmDeletedStocks,
  useConfirmTemporaryOpenDates,
  useConfirmUpdatedSchedules,
  useGeneratedDailyStocksByDefaultDayOfWeeks,
} from 'hooks/store'
import { saveStoreGeoSearch } from 'reducks/storeGeoSearch/operations'
import { StoreGeoSearch } from 'types/storeGeoSearch'

type Props = {
  storeId?: string
  page: 'regular' | 'close' | 'open' | 'shop'
}

const SettingsConfirmTemplate: FC<Props> = (props) => {
  const { storeId, page } = props
  const dispatch = useDispatch()

  const confirmStore: StoreType = useSelector((state: State) => getConfirmStore(state))
  const storeGeoSearch: StoreGeoSearch = useSelector((state: State) => getConfirmStoreGeoSearch(state))
  const breads: BreadType[] = useSelector((state: State) => getBreads(state))
  const stocks: StockType[] = useSelector((state: State) => getStocks(state))
  const schedules: ScheduleType[] = useSelector((state: State) => getSchedules(state))
  const holidays: DayOfWeekType[] = useSelector((state: State) => getHolidays(state))
  const confirmHolidays: DayOfWeekType[] = useSelector((state: State) => getConfirmHolidays(state))
  const openTime: OpenTimeType = useSelector((state: State) => getOpenTime(state))
  const confirmOpenTime: OpenTimeType = useSelector((state: State) => getConfirmOpenTime(state))
  const confirmOrderStartTime: BusinessTimeType = useSelector((state: State) => getConfirmOrderStartTime(state))
  const confirmLastOrderTime: BusinessTimeType = useSelector((state: State) => getConfirmLastOrderTime(state))
  const isOpenTimeUpdated = !equal(openTime, confirmOpenTime)

  const storeCalendars: StoreCalendarType[] = useSelector((state: State) => getStoreCalendars(state))
  const confirmStoreCalendars: StoreCalendarType[] = useSelector((state: State) => getConfirmStoreCalendars(state))

  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const specialHolidays: SpecialHolidayType[] = useSelector((state: State) => getSpecialHolidays(state))

  const checkoutItems = useCheckoutItemsFromOrders(orders)
  const addedHolidays = useConfirmAddedHolidays(confirmHolidays, holidays)
  const addedCloseStoreCalendars = useConfirmCloseStoreCalendars(
    confirmStoreCalendars,
    storeCalendars,
    holidays,
    specialHolidays
  )
  // 店舗オープン時刻の変更に伴う受渡解禁時刻の上書き・取得してupdatedSchedulesを更新
  const updatedSchedules = useConfirmUpdatedSchedules(schedules, confirmOpenTime, addedHolidays, isOpenTimeUpdated)
  // 追加された定休日に応じて削除が必要な在庫データを取得してdeletedStocksを更新
  const deletedStocks = useConfirmDeletedStocks(
    stocks,
    storeCalendars,
    addedHolidays,
    addedCloseStoreCalendars,
    checkoutItems,
    specialHolidays
  )
  // 臨時営業日に設定される日付を取得してtemporaryOpenDatesを更新
  const temporaryOpenDates = useConfirmTemporaryOpenDates(checkoutItems, storeCalendars, addedHolidays, specialHolidays)
  // 臨時営業日のデフォルト曜日設定によって自動で在庫生成される設定を取得
  const generatedDailyStocksByDefaultDayOfWeeks = useGeneratedDailyStocksByDefaultDayOfWeeks(
    schedules,
    storeCalendars,
    confirmStoreCalendars,
    page === 'open'
  )
  // 設定と在庫に変更のある商品を取得してbreadListを更新
  const breadList = useConfirmBreadList(
    breads,
    updatedSchedules,
    deletedStocks,
    stocks,
    temporaryOpenDates,
    generatedDailyStocksByDefaultDayOfWeeks
  )

  const saveSetting = useCallback(async () => {
    if (page === 'regular') {
      const isAdmin = false
      const { state, city, street, building } = confirmStore.address
      await dispatch(
        saveStoreGeoSearch({
          imageUrls: confirmStore.image_urls,
          storeAddress: state + city + street + building,
          storeId: storeRef.id,
          storeName: confirmStore.name,
          storeGeoSearch,
        })
      )
      await dispatch(
        saveStoreSettings({
          confirmOpenTime,
          confirmLastOrderTime,
          deletedStocks,
          updatedSchedules,
          temporaryOpenDates,
          isOpenTimeUpdated,
          storeRef,
          isAdmin,
        })
      )
      await dispatch(updatePreorderDeadline({ orderStartTime: confirmOrderStartTime, storeRef }))
    } else if (page === 'shop') {
      const isAdmin = typeof storeId !== 'undefined'
      const { state, city, street, building } = confirmStore.address
      await dispatch(
        saveStoreGeoSearch({
          imageUrls: confirmStore.image_urls,
          storeAddress: state + city + street + building,
          storeId: storeId || storeRef.id,
          storeName: confirmStore.name,
          storeGeoSearch,
        })
      )
      await dispatch(
        saveStoreSettings({
          confirmOpenTime,
          confirmLastOrderTime,
          deletedStocks,
          updatedSchedules,
          temporaryOpenDates,
          isOpenTimeUpdated,
          storeRef: storeId ? db.collection('stores').doc(storeId) : storeRef,
          isAdmin,
        })
      )
      await dispatch(updatePreorderDeadline({ orderStartTime: confirmOrderStartTime, storeRef }))
    } else {
      await dispatch(saveStoreCalendars(confirmStoreCalendars, deletedStocks, page))
    }
  }, [
    confirmStore,
    confirmOpenTime,
    confirmOrderStartTime,
    confirmLastOrderTime,
    deletedStocks,
    updatedSchedules,
    temporaryOpenDates,
    isOpenTimeUpdated,
    confirmStoreCalendars,
    page,
    storeRef,
    storeCalendars,
    storeId,
    storeGeoSearch,
  ])

  const backToSetting = useCallback(() => dispatch(goBack()), [])

  useEffect(() => {
    if (!storeRef && !storeId) return
    // スーパー管理者から編集する場合はstoreRefが存在しないのでstoreIdからrefを取得
    const ref = !storeRef && storeId ? db.collection('stores').doc(storeId) : storeRef
    dispatch(initOrders())
    dispatch(fetchBreads(ref, 'public'))
    dispatch(fetchSchedules(ref, ['daily', 'preorder']))
    dispatch(fetchStoreCalendars(ref, startOfMonth(new Date())))
    dispatch(fetchFutureStocks({ storeRef: ref, types: ['daily', 'preorder'] }))
    dispatch(fetchFutureOrders(ref, ['succeeded', 'requires_capture'], ['daily', 'preorder']))
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [storeRef, storeId])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'修正内容の確認'} />
      <Spacer size={20} />
      {breadList.length > 0 ? (
        <>
          <AlertCard
            description={'変更される箇所は赤字で表示しておりますので、必ずご確認いただき、保存を行ってください。'}
            headline={'すでに登録されている商品の内容が変更されます。'}
            title={'注意'}
          />
          <Spacer size={16} />
          <OrangeButton label={'保存する'} onClick={saveSetting} size={'medium'} type={'main'} />
          <Spacer size={48} />
          <SettingsConfirmCardList
            breadList={breadList}
            deletedStocks={deletedStocks}
            schedules={schedules}
            updatedSchedules={updatedSchedules}
            temporaryOpenDates={temporaryOpenDates}
            generatedDailyStocksByDefaultDayOfWeeks={generatedDailyStocksByDefaultDayOfWeeks}
          />
        </>
      ) : (
        <Description description={'すでに登録されている商品の内容に変更はありません。'} />
      )}
      <Spacer size={48} />
      <OrangeButton label={'保存する'} onClick={saveSetting} size={'medium'} type={'main'} />
      <Spacer size={20} />
      <NavButton
        label={page === 'shop' ? '店舗基本情報の編集へ戻る' : '営業時間・定休日の修正へ戻る'}
        iconPath={'/icon/grey/arrow_prev.svg'}
        onClick={backToSetting}
        url={''}
      />
      <Spacer size={72} />
    </div>
  )
}

export default SettingsConfirmTemplate
